// @ts-strict-ignore
import { useMemo } from "react";

import type {
  DashboardApiEndpointDefinitions,
  dashboardApiEndpoints,
  FilteredDashboardApiEndpointKeys,
  GetFetcherFunctionWithShadowError,
  UseDashboardApiQueryProps,
} from "@dashboardApi/apis";
import { useQuery } from "@tanstack/react-query";

import type { FetchRequestError } from "@lib/errors";
import type { Awaited } from "@lib/typeHelpers";

import { useAppGroupId } from "@services/useAppGroupId";
import { useErrorBoundary } from "@services/useErrorBoundary";
import { useInEditorDomain } from "@services/useInEditorDomain";
import { useRouteIsActive } from "@services/useRouteIsActive";

import { maxRetries } from "./constants";
import { useDashboardApi } from "./useDashboardApi";
import { useEditorDomainApiQueryFetcher } from "./useEditorDomainApiQueryFetcher";

/**
 * Based on the endpoint specified, this accepts arguments for the underlying GET fetcher function,
 * and options for react-query, and converts them into the query key and arguments to useQuery
 * @template Endpoint the endpoint name. Example: `"/engagement/get_changelogs"`
 * @template GetFetcherFunction an intermediary type to remove duplication in the type, this is the type of the fetcher function for `GET`
 * @template TData this is the return value of `select`, which is inferred from the `options` argument.
 */
export const useDashboardApiQuery = <
  Endpoint extends FilteredDashboardApiEndpointKeys,
  GetFetcherFunction extends DashboardApiEndpointDefinitions[Endpoint]["GET"],
  TFnData = Awaited<ReturnType<(typeof dashboardApiEndpoints)[Endpoint]["GET"]>>,
  TData = TFnData,
>(
  endpoint: Endpoint,
  {
    params,
    options,
  }: UseDashboardApiQueryProps<"GET", typeof dashboardApiEndpoints, Endpoint, GetFetcherFunction, TFnData, TData>
) => {
  const { logException } = useErrorBoundary();
  const appGroupId = useAppGroupId();
  const { GET: getFetcherFunction } = useDashboardApi(endpoint);
  const inEditorDomain = useInEditorDomain();
  const { isActive } = useRouteIsActive();
  // If we are in the editor domain, we will use the postMessageFetcher to send the request to the parent window.
  // The parent window will then send the request to the backend. This is because the editor domain is not allowed
  // to make network requests directly.
  const postMessageFetcher = useEditorDomainApiQueryFetcher(endpoint);
  const fetcherFunction = (args) => {
    // To comply with the react-query fetcher function signature, we will convert the args for the fetcher function
    // to an array before calling it.
    return (getFetcherFunction as any)(args, inEditorDomain ? postMessageFetcher : undefined);
  };
  const queryKey: Parameters<GetFetcherFunction>[0]["queryKey"] = [
    { cacheKey: endpoint, appGroupId },
    ...(params || []),
  ] as unknown as Parameters<GetFetcherFunction>[0]["queryKey"];
  const result = useQuery<
    TFnData,
    GetFetcherFunction extends GetFetcherFunctionWithShadowError<infer Error, any, any, any, any>
      ? Error
      : FetchRequestError,
    TData,
    Parameters<GetFetcherFunction>[0]["queryKey"]
  >(queryKey, fetcherFunction as any, {
    retry: (retryCount, error) => {
      if (error.status > 400 && error.status < 500) {
        return false;
      }
      return retryCount < maxRetries;
    },
    ...options,
    enabled: isActive && (options?.enabled ?? true),
    meta: {
      ...options?.meta,
      logException,
    },
  });
  // Keep a stable identity for the returned object across renders where
  // `result` itself is referentially stable — react-query already memoizes
  // its return value. Without this, the surrounding spread produced a fresh
  // object every render, and downstream consumers with `useEffect([query])`
  // deps (e.g. CampaignDigest setting `idleCount` from `query.data`)
  // re-ran every render. Under R18 strict effect scheduling that became a
  // visible regression — campaign data-table rows never settled. Same class
  // as BPE-91.
  return useMemo(
    () =>
      ({
        ...result,
        // polyfill the old behavior for components that rely upon the old behavior of `isLoading`
        isLoading: result.isInitialLoading,
      }) as typeof result,
    [result]
  );
};
